import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Upload, X, ExternalLink, Lock, AlertCircle, ShieldOff, Check, MailPlus } from 'lucide-react';
import { pmApi } from '../../lib/pmApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PO_STATUSES = ['Not Required', 'Needed', 'Requested', 'Received', 'Attached To Invoice', 'Rejected / Incorrect'];

const StatusBadge = ({ status }) => {
  const map = {
    'Not Required':         'bg-[#262626] text-[#A3A3A3] border-[#404040]',
    'Needed':               'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40',
    'Requested':            'bg-[#1E3A8A]/30 text-cyan-300 border-cyan-700/40',
    'Received':             'bg-emerald-900/30 text-emerald-300 border-emerald-700/40',
    'Attached To Invoice':  'bg-emerald-900/40 text-emerald-200 border-emerald-700/40',
    'Rejected / Incorrect': 'bg-red-900/30 text-red-300 border-red-700/40',
  };
  return <span data-testid="po-status-badge" className={`text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded ${map[status] || 'bg-[#262626] text-[#A3A3A3]'}`}>{status || 'Unknown'}</span>;
};

/**
 * Unified Purchase Order panel — used on PM Work Order Detail (mode='pm')
 * and Admin Work Order Detail (mode='admin').
 *
 * Props:
 *   request: the service-request doc (must include po_number, po_notes, po_status, po_file_id)
 *   mode: 'pm' | 'admin'
 *   onUpdated: called with the updated request after a save
 */
const PoPanel = ({ request: r, mode, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    po_number: r.po_number || '',
    po_notes: r.po_notes || '',
    po_internal_notes: r.po_internal_notes || '',
    po_status: r.po_status || 'Needed',
    po_file_id: r.po_file_id || null,
  });
  const [poFileMeta, setPoFileMeta] = useState(null);
  const [notReqDialog, setNotReqDialog] = useState(false);
  const [notReqReason, setNotReqReason] = useState('');

  const isAdmin = mode === 'admin';
  const fileUrl = form.po_file_id ? (isAdmin ? `${API}/admin/pm/po-files/${form.po_file_id}` : `${API}/pm/po-files/${form.po_file_id}`) : null;

  const upload = async (file) => {
    setBusy(true);
    try {
      const url = isAdmin ? `${API}/admin/pm/po-files/upload` : `${API}/pm/po-files/upload`;
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(url, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, po_file_id: data.file_id }));
      setPoFileMeta({ filename: data.filename, size: data.size });
      toast.success('PO uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const url = isAdmin ? `${API}/admin/pm/service-requests/${r.id}/po` : `${API}/pm/service-requests/${r.id}/po`;
      const body = isAdmin ? form : { po_number: form.po_number, po_notes: form.po_notes, po_file_id: form.po_file_id };
      const { data } = await axios.patch(url, body, { withCredentials: true });
      toast.success('PO saved');
      setEditing(false);
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setBusy(false);
  };

  const markNotRequired = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/pm/work-orders/${r.id}/po-not-required`, { reason: notReqReason }, { withCredentials: true });
      toast.success('Marked PO Not Required for this work order');
      setNotReqDialog(false); setNotReqReason('');
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  return (
    <div data-testid="po-panel" className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.22em] uppercase">Purchase Order</p>
          <StatusBadge status={r.po_status} />
        </div>
        {!editing && (
          <button data-testid="po-edit-btn" onClick={() => { setEditing(true); setForm({ po_number: r.po_number || '', po_notes: r.po_notes || '', po_internal_notes: r.po_internal_notes || '', po_status: r.po_status || 'Needed', po_file_id: r.po_file_id || null }); }} className="text-xs text-[#D4AF37] hover:text-[#F3E5AB]">
            {r.po_number ? 'Edit PO' : 'Add PO'}
          </button>
        )}
      </div>

      {!editing && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-0.5">PO Number</p>
            <p data-testid="po-number-view" className="text-white font-mono">{r.po_number || <span className="text-[#525252]">— not entered —</span>}</p>
          </div>
          <div>
            <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-0.5">PO Document</p>
            {fileUrl ? (
              <a data-testid="po-file-link" href={fileUrl} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:text-[#F3E5AB] inline-flex items-center gap-1 text-sm"><FileText className="w-3 h-3" /> View attachment <ExternalLink className="w-3 h-3" /></a>
            ) : <p className="text-[#525252] text-sm">—</p>}
          </div>
          {r.po_notes && (
            <div className="sm:col-span-2">
              <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-0.5">PO Notes</p>
              <p className="text-white whitespace-pre-wrap text-sm">{r.po_notes}</p>
            </div>
          )}
          {isAdmin && r.po_internal_notes && (
            <div className="sm:col-span-2">
              <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-0.5 flex items-center gap-1"><Lock className="w-3 h-3" /> Internal NexFlow Notes</p>
              <p className="text-white whitespace-pre-wrap text-sm bg-[#0A0A0A] border border-[#262626] rounded p-2">{r.po_internal_notes}</p>
            </div>
          )}
          {isAdmin && r.po_not_required_override && (
            <div className="sm:col-span-2 bg-[#262626]/40 border border-[#404040] rounded p-2">
              <p className="text-[#A3A3A3] text-xs">PO marked <strong>Not Required</strong> by {r.po_not_required_override.by_name} on {new Date(r.po_not_required_override.at).toLocaleDateString()}. Reason: {r.po_not_required_override.reason || '—'}</p>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-white text-sm">PO Number</label>
              <input data-testid="po-edit-number" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-white text-sm" placeholder="e.g. PO-12345" />
            </div>
            <div>
              <label className="text-white text-sm">PO Document</label>
              {form.po_file_id ? (
                <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded p-2">
                  <FileText className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white text-sm flex-1 truncate">{poFileMeta?.filename || 'PO attached'}</span>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, po_file_id: null }))} className="text-[#525252] hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label className="flex items-center gap-2 bg-[#0A0A0A] border border-dashed border-[#262626] rounded p-2 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">{busy ? 'Uploading…' : 'Upload PO'}</span>
                  <input data-testid="po-edit-upload" type="file" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                </label>
              )}
            </div>
          </div>
          <div>
            <label className="text-white text-sm">PO Notes</label>
            <textarea data-testid="po-edit-notes" rows={2} value={form.po_notes} onChange={(e) => setForm({ ...form, po_notes: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-white text-sm" />
          </div>
          {isAdmin && (
            <>
              <div>
                <label className="text-white text-sm flex items-center gap-1"><Lock className="w-3 h-3" /> Internal NexFlow Notes (admin-only)</label>
                <textarea data-testid="po-edit-internal-notes" rows={2} value={form.po_internal_notes} onChange={(e) => setForm({ ...form, po_internal_notes: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-white text-sm" />
              </div>
              <div>
                <label className="text-white text-sm">PO Status</label>
                <select data-testid="po-edit-status" value={form.po_status} onChange={(e) => setForm({ ...form, po_status: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-white text-sm">
                  {PO_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button data-testid="po-save-btn" disabled={busy} onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] px-4 h-9 rounded font-medium hover:bg-[#F3E5AB] disabled:opacity-40 inline-flex items-center gap-1 text-sm">
              <Check className="w-4 h-4" /> Save
            </button>
            <button onClick={() => setEditing(false)} className="text-[#A3A3A3] text-sm hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {isAdmin && r.po_status !== 'Not Required' && !notReqDialog && (
        <div className="pt-2 border-t border-[#262626] flex flex-wrap items-center gap-3">
          <button
            data-testid="po-request-from-billing-btn"
            disabled={busy}
            onClick={async () => {
              if (!window.confirm('Send a Quick-Approve link to the billing contact on file?\n\nThey will receive an email (and SMS if their consent is on file) with a one-tap link to enter the PO. Link expires in 7 days.')) return;
              setBusy(true);
              try {
                const { data } = await axios.post(`${API}/admin/pm/work-orders/${r.id}/request-po-from-billing`, {}, { withCredentials: true });
                const target = data.billing?.name || data.billing?.email || 'billing contact';
                toast.success(data.reused
                  ? `Re-used existing link for ${target} (sent via ${data.channels.join(', ')})`
                  : `Quick-Approve sent to ${target} via ${data.channels.join(', ')}`);
                onUpdated && onUpdated({ ...r, po_status: 'Requested' });
              } catch (e) {
                toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to send');
              }
              setBusy(false);
            }}
            className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MailPlus className="w-3 h-3" /> {busy ? 'Sending…' : 'Request PO from Billing'}
          </button>
          <button data-testid="po-not-required-btn" onClick={() => setNotReqDialog(true)} className="text-xs text-[#A3A3A3] hover:text-red-300 inline-flex items-center gap-1">
            <ShieldOff className="w-3 h-3" /> Mark PO Not Required (this work order only)
          </button>
        </div>
      )}

      {notReqDialog && (
        <div className="bg-[#7F1D1D]/10 border border-red-900 rounded p-3 space-y-2">
          <p className="text-red-300 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> One-shot exception. Company / property defaults are NOT changed. Logged to audit.</p>
          <textarea data-testid="po-not-required-reason" rows={2} value={notReqReason} onChange={(e) => setNotReqReason(e.target.value)} className="w-full bg-[#0A0A0A] border border-red-900 rounded p-2 text-white text-sm" placeholder="Reason (e.g., PM verbally approved, emergency repair)" />
          <div className="flex gap-2">
            <button data-testid="po-not-required-confirm" disabled={busy} onClick={markNotRequired} className="bg-red-700 text-white px-3 h-9 rounded text-sm hover:bg-red-600 disabled:opacity-40">Confirm Override</button>
            <button onClick={() => { setNotReqDialog(false); setNotReqReason(''); }} className="text-[#A3A3A3] text-sm hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      {(r.po_history || []).length > 0 && (
        <details className="text-xs">
          <summary className="text-[#525252] cursor-pointer hover:text-[#A3A3A3]">PO history ({r.po_history.length} events)</summary>
          <ul className="mt-2 space-y-1">
            {r.po_history.slice().reverse().map((h, i) => (
              <li key={i} className="text-[#A3A3A3] flex gap-2">
                <span className="text-[#525252]">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
                <span className="text-[#D4AF37]">{h.action.replace('po.', '')}</span>
                <span>by {h.by_name || 'system'}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default PoPanel;
